import {
  DagStatus,
  isRunningStatus,
  ProvisioningPlan,
  TaskAction,
} from '@agilelab/plugin-wb-builder-common';
import { Test } from '../../../ControlPanel';
import {
  CustomAlertApi,
  customAlertApiRef,
  poll,
  WbLink,
} from '@agilelab/plugin-wb-platform';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { FC, useEffect, useMemo, useState } from 'react';
import { panelCatalogApiRef } from '../../../../api';
import { useReleaseDetailPageContext } from '../../context/useReleaseDetailPageContext';
import { Deploy } from '../../types';
import { DeploySteps } from './DeploySteps';

export type MarketplaceProvisioningResult = {
  status: string;
  info?: {
    privateInfo?: {
      marketplaceInfo?: {
        type?: string;
        label?: string;
        href?: string;
        value?: string;
      };
    };
  };
};

const handleDeployFinishToast = (
  deploy: ProvisioningPlan & Deploy,
  marketplaceEnabled: boolean,
  alertApi: CustomAlertApi,
) => {
  const steps = deploy.dag.dependsOnTasks;
  const { action, status } = deploy.dag;
  if (status !== DagStatus.COMPLETED) return;
  let message = '';
  let link = undefined;
  if (action === 'PROVISION') {
    const dpInstanceData = steps.find(
      s => s.action === TaskAction.MARKETPLACE_UPDATE,
    );
    message = `Version ${deploy.version} successfully deployed`;
    if (marketplaceEnabled && dpInstanceData?.result) {
      const result = JSON.parse(dpInstanceData.result);
      const { info } = result as MarketplaceProvisioningResult;
      const href = info?.privateInfo?.marketplaceInfo?.href;
      if (href) link = <WbLink to={href}>Open in the marketplace</WbLink>;
    }
  } else if (action === 'UNPROVISION') {
    message = `Version ${deploy.version} successfully undeployed`;
  }

  if (message)
    alertApi.post({
      actions: link,
      message,
      severity: 'success',
      timeoutMillis: 5000,
    });
};

interface Props {
  deploy: Deploy;
}

const AsyncLatestDeployProcess: FC<Props> = ({ deploy }) => {
  const {
    fetchDeploys,
    setIsDeployingAll,
    setIsUndeployingAll,
    setSelectedDeploy,
    setSelectedStep,
    fetchDeploymentUnitStatus,
    setTestTaskId,
    testTaskId,
    refreshMarketPlaceLink,
    setIsValidationTestDrawerOpen,
    tests,
    fetchTests,
  } = useReleaseDetailPageContext();
  const panelCatalog = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const configApi = useApi(configApiRef);
  const [runningDeploy, setRunningDeploy] = useState<Deploy>(deploy);

  const pollingInterval =
    configApi.getOptionalNumber(
      'mesh.provisioner.provisioning.statusPolling.interval',
    ) ?? 3000;
  const marketplaceEnabled =
    configApi.getOptionalBoolean('mesh.marketplace.enabled') ?? true;

  useEffect(() => {
    const loadDeploy = () =>
      panelCatalog
        .getProvisioningPlan(deploy.id)
        .then(async provisioningPlan => ({
          ...deploy,
          ...provisioningPlan,
          deployEndDate: provisioningPlan.dag.stopTime,
          status: provisioningPlan.dag.status,
          steps: provisioningPlan.dag.dependsOnTasks,
        }));

    const controller = new AbortController();

    try {
      poll(
        loadDeploy,
        d => isRunningStatus(d.status),
        d => {
          // fetch the validation test as soon as the step finishes while deploy is still running
          if (
            d.steps[0].subPlanId &&
            d.steps[0].status !== DagStatus.RUNNING &&
            (!testTaskId || testTaskId !== d.steps[0].subPlanId)
          )
            setTestTaskId(d.steps[0].subPlanId);
          setSelectedDeploy(d);
          setSelectedStep(step => d.steps.find(s => s.id === step?.id));
          setRunningDeploy(d);
        },
        d => {
          fetchDeploys();
          refreshMarketPlaceLink();
          handleDeployFinishToast(d, marketplaceEnabled, alertApi);
          setIsDeployingAll(false);
          setIsUndeployingAll(false);
          fetchDeploymentUnitStatus();
        },
        { signal: controller.signal, ms: pollingInterval },
      );
    } catch (error) {
      alertApi.post({ error, severity: 'error' });
    }

    return () => controller.abort();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For a running deploy the testsMap is built from the single live-polled test
  const testsMap = useMemo(() => {
    const map = new Map<string, Test>();
    if (testTaskId && tests.value?.test) {
      map.set(testTaskId, tests.value.test);
    }
    return map;
  }, [testTaskId, tests.value?.test]);

  return (
    <DeploySteps
      deploy={runningDeploy}
      testsMap={testsMap}
      onStepClick={(step, d) => {
        setSelectedStep(step);
        setSelectedDeploy(d);
      }}
      onValidationGatewayClick={(d, subPlanId) => {
        setSelectedDeploy(d);
        if (subPlanId) setTestTaskId(subPlanId);
        fetchTests();
        setIsValidationTestDrawerOpen(true);
      }}
    />
  );
};

const StaticLatestDeployProcess: FC<Props> = ({ deploy }) => {
  const {
    setSelectedStep,
    setSelectedDeploy,
    setIsValidationTestDrawerOpen,
    setTestTaskId,
    tests,
    testTaskId,
  } = useReleaseDetailPageContext();

  const testsMap = useMemo(() => {
    const map = new Map<string, Test>();
    if (testTaskId && tests.value?.test) {
      map.set(testTaskId, tests.value.test);
    }
    return map;
  }, [testTaskId, tests.value?.test]);

  return (
    <DeploySteps
      deploy={deploy}
      testsMap={testsMap}
      onStepClick={(step, d) => {
        setSelectedStep(step);
        setSelectedDeploy(d);
      }}
      onValidationGatewayClick={(d, subPlanId) => {
        setSelectedDeploy(d);
        if (subPlanId) setTestTaskId(subPlanId);
        setIsValidationTestDrawerOpen(true);
      }}
    />
  );
};

export const LatestDeployProcess: FC<Props> = ({ deploy }) => {
  const isRunning = useMemo(() => isRunningStatus(deploy.status), [deploy]);

  return isRunning ? (
    <AsyncLatestDeployProcess deploy={deploy} />
  ) : (
    <StaticLatestDeployProcess deploy={deploy} />
  );
};
